import React, { ChangeEvent } from "react";
import TextField from "@material-ui/core/TextField";
import Autocomplete, {
  AutocompleteChangeReason,
  AutocompleteChangeDetails,
} from "@material-ui/lab/Autocomplete";
import { makeStyles } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import { countries } from "../../constants";

interface Props {
  country: string;
  selectCountry: (country: CountryType) => void;
  error: string | undefined | null;
}

export interface CountryType {
  code: string;
  country: string;
  continent: string;
}

function countryToFlag(isoCode: string) {
  return typeof String.fromCodePoint !== "undefined"
    ? isoCode
        .toUpperCase()
        .replace(/./g, (char) =>
          String.fromCodePoint(char.charCodeAt(0) + 127397)
        )
    : isoCode;
}

const useStyles = makeStyles({
  option: {
    fontSize: 15,
    "& > span": {
      marginRight: 10,
      fontSize: 18,
    },
  },
  textField: {
    width: "250px",
  },
});

export default function CountryPicker({
  country,
  selectCountry,
  error,
}: Props) {
  const classes = useStyles();
  const { t } = useTranslation();
  const countryType = countries.find((c) => c.country === country);

  const handleChange = (
    _event: ChangeEvent<{}>,
    value: CountryType | null,
    _reason: AutocompleteChangeReason,
    _details?: AutocompleteChangeDetails<CountryType> | undefined
  ) => {
    if (value) selectCountry(value);
  };

  return (
    <Autocomplete
      id="country-autocomplete"
      options={countries as CountryType[]}
      classes={{
        option: classes.option,
      }}
      autoHighlight
      getOptionLabel={(option) => option.country}
      renderOption={(option) => (
        <>
          <span>{countryToFlag(option.code)}</span>
          {option.country} ({option.code})
        </>
      )}
      onChange={handleChange}
      value={countryType}
      renderInput={(params) => (
        <TextField
          {...params}
          id="country"
          name="country"
          label={t("signup.country")}
          variant="outlined"
          inputProps={{
            ...params.inputProps,
            autoComplete: "new-password", // disable autocomplete and autofill
          }}
          required
          error={error ? true : false}
          helperText={error}
          className={classes.textField}
        />
      )}
    />
  );
}
